import { Form, Spin } from "antd"
import { useEffect, useState } from "react"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { stripTripPayloadKeys } from "layoutsV2/pro/ProNewListingLayout/tripPayload"
import { serialize } from "object-to-formdata"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updateTrip, getGuideTrip, getCategories, getTripCategories } from "store/trips"
import { CustomForm } from "ui/Form"
import { AdditionalDetails } from "./AdditionalDetails"
import { ExperienceDetails } from "./ExperienceDetails"
import { ExperienceInfo } from "./ExperienceInfo"
import { PublishingSettings } from "./PublishingSettings"
import { AddOns } from "./AddOns/AddOns"
import { setFormValues, setFormTouched } from "store/proNewListingForms"

export const ListingDetails = () => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const trip = useSelector(modelSelector("trip"))
  const tripLoading = useSelector((state) => state.trips.trip.loading)
  const [selectedLanguages, setSelectedLanguages] = useState([])
  const [selectedSkillLevels, setSelectedSkillLevels] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const formDetails = useSelector((state) => state.proNewListingForms.details)
  const [isFormInitialized, setIsFormInitialized] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
      dispatch(getCategories())
      dispatch(getTripCategories())
    }
  }, [dispatch, id])

  // Reset initialization flag when navigating to a different trip
  useEffect(() => {
    setIsFormInitialized(false)
  }, [id])

  useEffect(() => {
    // Only initialize if we have trip data AND it matches the current ID
    if (trip?.id === Number(id) && !isFormInitialized) {
      let categories = []
      if (Array.isArray(trip.categories)) {
        categories = trip.categories
      } else if (trip.categories) {
        categories = [trip.categories]
      }
      form.setFieldsValue({
        title: trip.title,
        description: trip.description,
        categories: categories.length ? categories : undefined,
        min_guests: trip.min_guests,
        max_guests: trip.max_guests,
        languages: trip.languages,
        skill_levels: trip.skill_levels,
        included_items: trip.included_items,
        bring_items: trip.bring_items,
        accessibility_enabled: trip.accessibility_enabled,
        alcohol_allowed: trip.alcohol_allowed,
        children_allowed: trip.children_allowed,
        publish_on_guides_site: trip.publish_on_guides_site,
        publish_on_platform: trip.publish_on_platform,
        add_on_ids: trip.add_on_ids || trip.assigned_add_on_ids || (trip.add_ons || []).map((a) => a.id)
      })
      setIsFormInitialized(true)

      // Reset touched state to false after initialization (edit mode)
      // This prevents auto-save when switching tabs without making changes
      if (id) {
        dispatch(setFormTouched("details", false))
      }
    }
  }, [trip, form, id, dispatch, isFormInitialized])

  const onFinish = (callback) => {
    if (!id) return

    form.validateFields().then(() => {
      const formData = createFormData()
      handleFormSubmission(formData, callback)
    })
  }

  const createFormData = () => {
    const formValues = stripTripPayloadKeys(form.getFieldsValue(true))

    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []
    const payload = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id })),
      wizard_step: "details"
    }
    return serialize({ trip: payload }, { allowEmptyArrays: true })
  }

  const handleFormSubmission = (formData, callback) => {
    setIsLoading(true)
    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("details", false))
        setIsLoading(false)
        callback?.()
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    const formValues = form.getFieldsValue()
    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []
    const allValues = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id }))
    }
    dispatch(setFormValues("details", allValues))
  }, [dispatch, trip, selectedLanguages, selectedSkillLevels, form])

  const handleValuesChange = (_, allValues) => {
    const currentValues = {
      ...formDetails?.values,
      ...allValues
    }

    dispatch(setFormValues("details", currentValues))
    // Only mark as touched if initialization is complete
    if (isFormInitialized && !formDetails?.touched) {
      dispatch(setFormTouched("details", true))
    }
  }

  const handleLanguageInteraction = () => {
    dispatch(setFormTouched("details", true))
  }

  const handleSkillInteraction = () => {
    dispatch(setFormTouched("details", true))
  }

  const handleDescriptionChange = () => {
    // Sync current form values to Redux (including the new description)
    const formValues = form.getFieldsValue()
    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []
    const allValues = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id }))
    }
    dispatch(setFormValues("details", allValues))
    dispatch(setFormTouched("details", true))
  }

  return (
    <Spin spinning={tripLoading || isLoading} size="large" style={{ width: "100%" }}>
      <CustomForm
        form={form}
        onValuesChange={handleValuesChange}
        initialValues={trip}
        layout="vertical"
        style={{ maxWidth: "984px" }}
      >
        <ExperienceInfo form={form} onDescriptionChange={handleDescriptionChange} />
        <ExperienceDetails
          form={form}
          setSelectedLanguages={setSelectedLanguages}
          selectedLanguages={selectedLanguages}
          selectedSkillLevels={selectedSkillLevels}
          setSelectedSkillLevels={setSelectedSkillLevels}
          onLanguageInteraction={handleLanguageInteraction}
          onSkillInteraction={handleSkillInteraction}
        />
        <AdditionalDetails form={form} />
        <AddOns form={form} isFormInitialized={isFormInitialized} />
        <PublishingSettings form={form} />
      </CustomForm>
      <ListingActionButtons form={form} onFinish={onFinish} />
    </Spin>
  )
}
